import * as Yup from "yup";
import {
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  Divider,
  Typography,
} from "@mui/material";
import { capitalize } from "common/utils/strings";
import { ConfiguredJob, rtcJobsApi } from "common/state/rtcJobsApi";
import { DimensionedNumberInput } from "common/components/DimensionedNumberInput";
import { Form, Formik } from "formik";
import { Job } from "protos/rtc/jobs";
import { JobType } from "protos/atk/job_type";
import { useMutationPopups } from "portal/utils/hooks/useApiPopups";
import { useSelf } from "portal/state/store";
import { useTranslation } from "react-i18next";
import InfoIcon from "@mui/icons-material/Info";
import React, { FC } from "react";

interface SpeedEditModalProps {
  job: Job;
  open: boolean;
  onSave?: () => void;
  onClose?: () => void;
}

interface SpeedFormValues {
  toolSpeed: number;
  transitionSpeed: number;
  rowTraversalSpeed: number;
  transportSpeed: number;
}

interface CurrentSpeeds {
  toolSpeedMS?: number;
  transitionSpeedMS?: number;
  rowTraversalSpeedMS?: number;
  transportSpeedMS?: number;
}

const getCurrentSpeeds = (configuredJob?: ConfiguredJob): CurrentSpeeds => {
  switch (configuredJob?.job.type) {
    case JobType.IN_SEASON: {
      return {
        toolSpeedMS: configuredJob.inSeason?.toolSpeedMS,
        transitionSpeedMS: configuredJob.inSeason?.transitionSpeedMS,
        rowTraversalSpeedMS: configuredJob.inSeason?.rowTraversalSpeedMS,
      };
    }
    case JobType.GROUND_PREP: {
      return {
        toolSpeedMS: configuredJob.groundPrep?.pathPlan?.toolSpeedMS,
        transitionSpeedMS:
          configuredJob.groundPrep?.pathPlan?.transitionSpeedMS,
      };
    }
    case JobType.LASER_WEED: {
      return {
        transitionSpeedMS: configuredJob.laserWeed?.transitionSpeedMS,
        rowTraversalSpeedMS: configuredJob.laserWeed?.rowTraversalSpeedMS,
      };
    }
    case JobType.TRANSPORT: {
      return { transportSpeedMS: configuredJob.transport?.transportSpeedMS };
    }
    default: {
      return {};
    }
  }
};

export const SpeedEditModal: FC<SpeedEditModalProps> = ({
  job,
  open,
  onClose,
  onSave,
}) => {
  const { t } = useTranslation();
  const { measurementSystem } = useSelf();
  const { data: configuredJob, isLoading: loadingJob } =
    rtcJobsApi.endpoints.getConfiguredJob.useQuery({
      id: job.id.toString(),
    });

  const isGroundPrep = job.type === JobType.GROUND_PREP;
  const isLaserWeed = job.type === JobType.LASER_WEED;
  const isTransport = job.type === JobType.TRANSPORT;
  const isInSeason = job.type === JobType.IN_SEASON;

  const [mutateToolSpeed] = useMutationPopups(
    rtcJobsApi.endpoints.setGroundPrepToolSpeed.useMutation(),
    {
      success: capitalize(
        t("utils.actions.updatedLong", {
          subject: t("models.equipment.implement.toolSpeed"),
        })
      ),
    }
  );

  const [mutateTransitionSpeed] = useMutationPopups(
    rtcJobsApi.endpoints.setTransitionSpeed.useMutation(),
    {
      success: capitalize(
        t("utils.actions.updatedLong", {
          subject: t("models.equipment.implement.transitionSpeed"),
        })
      ),
    }
  );

  const [mutateRowTraversalSpeed] = useMutationPopups(
    rtcJobsApi.endpoints.setRowTraversalSpeed.useMutation(),
    {
      success: capitalize(
        t("utils.actions.updatedLong", {
          subject: t("models.equipment.implement.rowTraversalSpeed"),
        })
      ),
    }
  );

  const [mutateTransportSpeed] = useMutationPopups(
    rtcJobsApi.endpoints.setTransportSpeed.useMutation(),
    {
      success: capitalize(
        t("utils.actions.updatedLong", {
          subject: t("models.equipment.implement.transportSpeed"),
        })
      ),
    }
  );

  const currentSpeeds = getCurrentSpeeds(configuredJob);

  const initialValues: SpeedFormValues = {
    toolSpeed: currentSpeeds.toolSpeedMS ?? 0,
    transitionSpeed: currentSpeeds.transitionSpeedMS ?? 0,
    rowTraversalSpeed: currentSpeeds.rowTraversalSpeedMS ?? 0,
    transportSpeed: currentSpeeds.transportSpeedMS ?? 0,
  };

  const validationSchema = Yup.object({
    toolSpeed:
      isGroundPrep || isInSeason
        ? Yup.number()
            .required(t("utils.form.required"))
            .min(0, t("utils.form.mustBeGreaterThan", { min: 0 }))
        : Yup.number(),
    transitionSpeed:
      isGroundPrep || isLaserWeed || isInSeason
        ? Yup.number()
            .required(t("utils.form.required"))
            .min(0, t("utils.form.mustBeGreaterThan", { min: 0 }))
        : Yup.number(),
    rowTraversalSpeed:
      isLaserWeed || isInSeason
        ? Yup.number()
            .required(t("utils.form.required"))
            .min(0, t("utils.form.mustBeGreaterThan", { min: 0 }))
        : Yup.number(),
    transportSpeed: isTransport
      ? Yup.number()
          .required(t("utils.form.required"))
          .min(0, t("utils.form.mustBeGreaterThan", { min: 0 }))
      : Yup.number(),
  });

  const handleSubmit = async (values: SpeedFormValues): Promise<void> => {
    if (
      (isGroundPrep || isLaserWeed || isInSeason) &&
      values.transitionSpeed !== currentSpeeds.transitionSpeedMS
    ) {
      await mutateTransitionSpeed({
        transitionSpeedMS: values.transitionSpeed,
        id: job.id,
      });
    }

    if (
      (isGroundPrep || isInSeason) &&
      values.toolSpeed !== currentSpeeds.toolSpeedMS
    ) {
      await mutateToolSpeed({
        toolSpeedMS: values.toolSpeed,
        id: job.id,
      });
    }

    if (
      (isLaserWeed || isInSeason) &&
      values.rowTraversalSpeed !== currentSpeeds.rowTraversalSpeedMS
    ) {
      await mutateRowTraversalSpeed({
        rowTraversalSpeedMS: values.rowTraversalSpeed,
        id: job.id,
      });
    }

    if (
      isTransport &&
      values.transportSpeed !== currentSpeeds.transportSpeedMS
    ) {
      await mutateTransportSpeed({
        transportSpeedMS: values.transportSpeed,
        id: job.id,
      });
    }
    onSave?.();
  };

  return (
    <Dialog open={open} fullWidth>
      <Formik
        initialValues={initialValues}
        validationSchema={validationSchema}
        onSubmit={handleSubmit}
        enableReinitialize
      >
        {({ values, setFieldValue, isSubmitting, handleReset, dirty }) => (
          <Form>
            <DialogContent className="flex flex-col gap-2">
              {isGroundPrep && (
                <>
                  <div className="flex gap-1">
                    <InfoIcon fontSize="small" />
                    <Typography className="flex my-auto">
                      {t("views.autotractor.jobDetails.editSpeedWarning")}
                    </Typography>
                  </div>
                  <Divider />
                </>
              )}
              {loadingJob ? (
                <CircularProgress />
              ) : (
                <div className="flex flex-col gap-4">
                  {(isGroundPrep || isInSeason) && (
                    <div className="flex justify-between">
                      <Typography className="flex my-auto">
                        {capitalize(t("models.equipment.implement.toolSpeed"))}
                      </Typography>
                      <DimensionedNumberInput
                        label=""
                        storedValue={values.toolSpeed}
                        storedUnit={"m/s"}
                        displayUnits={{
                          metric: "km/h",
                          imperial: "mph",
                        }}
                        localSystem={measurementSystem}
                        onChange={(value: number | undefined) => {
                          setFieldValue("toolSpeed", value ?? 0);
                        }}
                      />
                    </div>
                  )}
                  {(isGroundPrep || isLaserWeed || isInSeason) && (
                    <div className="flex justify-between">
                      <Typography className="flex my-auto">
                        {capitalize(
                          t("models.equipment.implement.transitionSpeed")
                        )}
                      </Typography>
                      <DimensionedNumberInput
                        label=""
                        storedValue={values.transitionSpeed}
                        storedUnit={"m/s"}
                        displayUnits={{
                          metric: "km/h",
                          imperial: "mph",
                        }}
                        localSystem={measurementSystem}
                        onChange={(value: number | undefined) => {
                          setFieldValue("transitionSpeed", value ?? 0);
                        }}
                      />
                    </div>
                  )}
                  {(isLaserWeed || isInSeason) && (
                    <div className="flex justify-between">
                      <Typography className="flex my-auto">
                        {capitalize(
                          t("models.equipment.implement.rowTraversalSpeed")
                        )}
                      </Typography>
                      <DimensionedNumberInput
                        label=""
                        storedValue={values.rowTraversalSpeed}
                        storedUnit={"m/s"}
                        displayUnits={{
                          metric: "km/h",
                          imperial: "mph",
                        }}
                        localSystem={measurementSystem}
                        onChange={(value: number | undefined) => {
                          setFieldValue("rowTraversalSpeed", value ?? 0);
                        }}
                      />
                    </div>
                  )}
                  {isTransport && (
                    <div className="flex justify-between">
                      <Typography className="flex my-auto">
                        {capitalize(
                          t("models.equipment.implement.transportSpeed")
                        )}
                      </Typography>
                      <DimensionedNumberInput
                        label=""
                        storedValue={values.transportSpeed}
                        storedUnit={"m/s"}
                        displayUnits={{
                          metric: "km/h",
                          imperial: "mph",
                        }}
                        localSystem={measurementSystem}
                        onChange={(value: number | undefined) => {
                          setFieldValue("transportSpeed", value ?? 0);
                        }}
                      />
                    </div>
                  )}
                </div>
              )}
            </DialogContent>
            <DialogActions>
              {isSubmitting && (
                <CircularProgress
                  color="info"
                  size={"16px"}
                  className="flex my-auto"
                />
              )}
              <Button
                disabled={isSubmitting || !dirty}
                variant="contained"
                type="submit"
              >
                {t("utils.actions.save")}
              </Button>
              <Button
                disabled={isSubmitting}
                onClick={() => {
                  handleReset();
                  onClose?.();
                }}
              >
                {t("utils.actions.cancel")}
              </Button>
            </DialogActions>
          </Form>
        )}
      </Formik>
    </Dialog>
  );
};
